import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { fetchGanttChartByLocation } from "../../API_Service/LabBooking_Slice";

/**
 * This uses the classic Kendo UI for jQuery Gantt widget (`kendoGantt`),
 * loaded globally via <script src=".../assets/kendo/js/kendo.all.min.js">
 * and jQuery in public/index.html — NOT the @progress/kendo-react-gantt
 * npm package. That's why the screenshot shows the jQuery widget's own
 * toolbar/treelist/filter-row chrome rather than a React component tree.
 *
 * Nothing extra needs to be installed — window.$ and window.kendo are
 * already available by the time this component mounts.
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
function parseDate(s) {
  if (!s || s === "NA") return null;
  const m = s.match(/(\d{2})-(\d{2})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (!m) return null;
  const [, dd, mm, yyyy, hh, min, ampm] = m;
  let hours = +hh % 12;
  if (ampm.toUpperCase() === "PM") hours += 12;
  return new Date(+yyyy, +mm - 1, +dd, hours, +min);
}
function toFraction(pct) {
  const n = Number(pct);
  return Number.isFinite(n) ? n : 0;
}
function getStatus(pct) {
  const p = Math.round((pct || 0) * 100);
  if (p >= 100) return "completed";
  if (p > 0) return "running";
  return "pending";
}
const STATUS_COLOR = {
  completed: "#16a34a",
  running: "#2563eb",
  pending: "#94a3b8",
};
const STATUS_BADGE = {
  completed: "✓ Completed",
  running: "▶ Running",
  pending: "○ Pending",
};
const TYPE_COLOR = {
  loc: "#1a56a0",
  fac: "#16a34a",
  proj: "#d97706",
  task: "#7c3aed",
};
const TYPE_WEIGHT = { loc: 700, fac: 600, proj: 500, task: 400 };

// App primary brand gradient (matches the teal used elsewhere in the app,
// e.g. LabBookingIndex's modal header: from-[#08757c] to-[#005b60])
const PRIMARY_GRADIENT =
  "linear-gradient(135deg, rgb(8, 117, 124), rgb(0, 91, 96))";
const PRIMARY = "#08757c";

// ─── flat lists → flat Kendo GanttDataSource rows (id/parentId) ───────────────
// The jQuery Gantt's DataSource is flat with id/parentId links (unlike the
// KendoReact package's nested `subtasks` tree), so this maps straight from
// the four API lists without building a nested structure.
//
// filterFrom/filterTo should already be normalized to start-of-day /
// end-of-day (see callers) so a single exact date (from === to) still
// matches records that fall anywhere within that day.
function buildFlatTasks(data, filterFrom, filterTo) {
  const rows = [];
  let order = 0;

  // A node is in range if its own [start, end] overlaps the filter window
  // at all. If either the filter or the node's own dates are missing, don't
  // exclude it — only exclude when we can actually compare real dates.
  const inRange = (s, e) => {
    if (!filterFrom || !filterTo) return true;
    if (!s || !e) return false;
    return !(e < filterFrom || s > filterTo);
  };

  (data.mainList || []).forEach((loc) => {
    const ls = parseDate(loc.StartDate);
    const le = parseDate(loc.EndDate);
    // Drop the whole location (and everything under it) if it doesn't
    // overlap the selected range at all — this is what was missing before,
    // and why locations with no bookings in range were still showing up.
    if (!inRange(ls, le)) return;

    const locId = "loc_" + loc.LocationId;
    rows.push({
      id: locId,
      parentId: null,
      orderId: order++,
      title: loc.Title,
      start: ls || new Date(),
      end: le || new Date(),
      percentComplete: toFraction(loc.LocationCompletedPercentage),
      summary: true,
      expanded: false, // collapsed by default, matching the original component
      rowType: "loc",
    });

    (data.parentList || [])
      .filter((f) => f.LocationId === loc.LocationId)
      .forEach((fac) => {
        const fs = parseDate(fac.StartDate);
        const fe = parseDate(fac.EndDate);
        if (!inRange(fs, fe)) return;

        const facId = "fac_" + fac.FacilityId + "_" + fac.LocationId;
        rows.push({
          id: facId,
          parentId: locId,
          orderId: order++,
          title: fac.Title,
          start: fs || new Date(),
          end: fe || new Date(),
          percentComplete: toFraction(fac.FacilityCompletedPercentage),
          summary: true,
          expanded: false,
          rowType: "fac",
        });

        (data.projectList || [])
          .filter(
            (p) =>
              p.FacilityId === fac.FacilityId &&
              p.LocationId === fac.LocationId,
          )
          .forEach((proj) => {
            const ps = parseDate(proj.StartDate);
            const pe = parseDate(proj.EndDate);
            if (!inRange(ps, pe)) return;

            const projId =
              "proj_" +
              proj.ProjectId +
              "_" +
              fac.FacilityId +
              "_" +
              proj.LocationId;
            rows.push({
              id: projId,
              parentId: facId,
              orderId: order++,
              title: proj.Title,
              start: ps || new Date(),
              end: pe || new Date(),
              percentComplete: toFraction(proj.ProjectCompletedPercentage),
              summary: true,
              expanded: false,
              rowType: "proj",
            });

            (data.subList || [])
              .filter(
                (t) =>
                  t.ProjectId === proj.ProjectId &&
                  t.FacilityId === proj.FacilityId &&
                  (t.LocationId === undefined ||
                    t.LocationId === loc.LocationId),
              )
              .forEach((task) => {
                const ts = parseDate(task.StartDate);
                const te = parseDate(task.EndDate);
                if (!inRange(ts, te)) return;

                rows.push({
                  id:
                    "task_" +
                    task.ProjectTestDetId +
                    "_" +
                    proj.ProjectId +
                    "_" +
                    fac.FacilityId +
                    "_" +
                    loc.LocationId,
                  parentId: projId,
                  orderId: order++,
                  title: task.Title,
                  start: ts || new Date(),
                  end: te || new Date(),
                  percentComplete: toFraction(task.TestCompletedPercentage),
                  summary: false,
                  expanded: false,
                  rowType: "task",
                });
              });
          });
      });
  });

  return rows;
}

// ─── column cell templates (Kendo micro-templates, receive the data item) ────
function taskCellTemplate(dataItem) {
  const color = TYPE_COLOR[dataItem.rowType] || "#1e293b";
  const weight = TYPE_WEIGHT[dataItem.rowType] || 400;
  const encode = window.kendo ? window.kendo.htmlEncode : (s) => s;
  return `<span style="color:${color};font-weight:${weight};">${encode(dataItem.title || "")}</span>`;
}

function percentCellTemplate(dataItem) {
  const pct = toFraction(dataItem.percentComplete);
  const p = Math.round(pct * 100);
  const status = getStatus(pct);
  const color = STATUS_COLOR[status];
  return `
    <div style="display:flex;flex-direction:column;align-items:center;gap:3px;padding:2px 4px;">
      <div style="display:flex;align-items:center;gap:4px;">
        <span style="font-size:9px;font-weight:700;letter-spacing:.03em;color:#fff;background:${color};padding:1px 5px;border-radius:99px;">${STATUS_BADGE[status]}</span>
        <span style="font-size:11px;font-weight:700;color:${color};">${p}%</span>
      </div>
      <div style="width:100%;height:5px;background:#e2e8f0;border-radius:99px;overflow:hidden;">
        <div style="width:${p}%;height:100%;background:${color};border-radius:99px;"></div>
      </div>
    </div>`;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function LabBookingGanttKendoJQuery({
  companyID,
  onAddClick,
  data: dataProp,
  loading: loadingProp,
}) {
  const dispatch = useDispatch();
  const [internalData, setInternalData] = useState({
    mainList: [],
    parentList: [],
    projectList: [],
    subList: [],
  });
  const [internalLoading, setInternalLoading] = useState(false);
  const data = dataProp || internalData;
  const loading = loadingProp ?? internalLoading;

  const todayStr = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(todayStr);
  const [toDate, setToDate] = useState(todayStr);

  const ganttElRef = useRef(null);
  const widgetRef = useRef(null);
  const [dateError, setDateError] = useState("");

  const handleFromDateChange = (val) => {
    setFromDate(val);
    if (toDate && val > toDate) {
      setDateError("Start date cannot be later than end date.");
    } else {
      setDateError("");
    }
  };

  const handleToDateChange = (val) => {
    if (fromDate && val < fromDate) {
      setDateError("End date cannot be earlier than start date.");
      setToDate(val); // still reflect what they picked, but flag it
      return;
    }
    setToDate(val);
    setDateError("");
  };

  // Re-skin Kendo's own "selected" state (used by the Day/Week/Month view
  // buttons) to the app's primary teal instead of the default theme color.
  // Scoped to .lab-gantt-kendo-jquery so it doesn't affect other Kendo
  // widgets elsewhere on the page.
  useEffect(() => {
    if (document.getElementById("lab-gantt-kendo-theme")) return;
    const style = document.createElement("style");
    style.id = "lab-gantt-kendo-theme";
    style.textContent = `
      .lab-gantt-kendo-jquery .k-button-solid-base.k-selected,
      .lab-gantt-kendo-jquery .k-button-solid-base.k-selected:hover,
      .lab-gantt-kendo-jquery .k-gantt .k-state-selected,
      .lab-gantt-kendo-jquery .k-gantt .k-button.k-state-selected,
      .lab-gantt-kendo-jquery .k-gantt .k-state-selected:hover {
        border-color: ${PRIMARY} !important;
        color: #fff !important;
        background-color: ${PRIMARY} !important;
        background: ${PRIMARY_GRADIENT} !important;
      }  .lab-gantt-kendo-jquery .k-gantt .k-task-summary,
      .lab-gantt-kendo-jquery .k-gantt .k-task-summary-complete,
      .lab-gantt-kendo-jquery .k-gantt .k-task-summary::before,
      .lab-gantt-kendo-jquery .k-gantt .k-task-summary::after {
        background-color: ${PRIMARY} !important;
        border-color: ${PRIMARY} !important;
      }
      .lab-gantt-kendo-jquery .k-gantt .k-task-single,
      .lab-gantt-kendo-jquery .k-gantt .k-task-complete,
      .lab-gantt-kendo-jquery .k-gantt .k-task-template {
        background-color: ${PRIMARY} !important;
        border-color: ${PRIMARY} !important;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (!companyID) return;
    if (dataProp) return;
    if (fromDate && toDate && fromDate > toDate) return;
    setInternalLoading(true);
    dispatch(
      fetchGanttChartByLocation({
        startDateTime: fromDate,
        endDateTime: toDate,
        companyID,
      }),
    )
      .unwrap()
      .then((res) => setInternalData(res?.data ?? res))
      .catch(console.error)
      .finally(() => setInternalLoading(false));
  }, [fromDate, toDate, companyID]); // eslint-disable-line

  const isEmpty = (data.mainList || []).length === 0;

  // Normalize to start-of-day / end-of-day so a single exact date (e.g.
  // fromDate === toDate === "2026-06-23") still matches a record timestamped
  // anywhere within that day, instead of only matching midnight exactly.
  const filterFrom = fromDate ? new Date(fromDate + "T00:00:00.000") : null;
  const filterTo = toDate ? new Date(toDate + "T23:59:59.999") : null;

  // ── (Re)build the jQuery kendoGantt widget whenever the data changes ───────
  useEffect(() => {
    const $ = window.$ || window.jQuery;
    const kendoLib = window.kendo;
    if (!$ || !kendoLib || !ganttElRef.current) return;
    if (loading || isEmpty) return;
    if (fromDate && toDate && fromDate > toDate) return;
    const $el = $(ganttElRef.current);

    // Destroy ANY kendoGantt already bound to this DOM node — not just the
    // instance our own ref thinks it created. React 18 StrictMode
    // double-invokes effects in development (mount → cleanup → mount), and
    // relying only on widgetRef can leave a stray widget behind if that
    // cleanup/re-mount timing doesn't line up. Querying jQuery's own data
    // cache on the element is the reliable way to catch that.
    const existingWidget = $el.data("kendoGantt");
    if (existingWidget) {
      existingWidget.destroy();
    }
    if (widgetRef.current && widgetRef.current !== existingWidget) {
      widgetRef.current.destroy();
    }
    $el.empty();
    widgetRef.current = null;

    const tasks = buildFlatTasks(data, filterFrom, filterTo);

    // Debug: confirm real rows are actually being built before handing them
    // to kendoGantt. If this logs an empty array or missing dates, the bug
    // is upstream in buildFlatTasks/your API data, not the widget config.
    console.log("Gantt tasks:", tasks);

    try {
      $el.kendoGantt({
        dataSource: {
          data: tasks,
          schema: {
            model: {
              id: "id",
              fields: {
                id: { type: "string" },
                parentId: { type: "string", defaultValue: null },
                orderId: { type: "number" },
                title: { type: "string" },
                start: { type: "date" },
                end: { type: "date" },
                percentComplete: { type: "number" },
                summary: { type: "boolean" },
                expanded: { type: "boolean" },
                rowType: { type: "string" },
              },
            },
          },
        },
        columns: [
          {
            field: "title",
            title: "Task",
            width: 260,
            editable: false,
            template: taskCellTemplate,
          },
          {
            field: "start",
            title: "Start Date",
            format: "{0:dd-MM-yyyy}",
            width: 110,
            editable: false,
            attributes: { style: "text-align:center" },
            headerAttributes: { style: "text-align:center" },
          },
          {
            field: "end",
            title: "End Date",
            format: "{0:dd-MM-yyyy}",
            width: 110,
            editable: false,
            attributes: { style: "text-align:center" },
            headerAttributes: { style: "text-align:center" },
          },
          {
            field: "percentComplete",
            title: "% Complete",
            width: 150,
            editable: false,
            template: percentCellTemplate,
            attributes: { style: "text-align:center;padding:0;" },
            headerAttributes: { style: "text-align:center" },
          },
        ],
        views: ["day", "week", "month"],
        editable: false,
        filterable: true,
        rowHeight: 40,
        showWorkHours: false,
        showWorkDays: false,
      });
      widgetRef.current = $el.data("kendoGantt");
    } catch (err) {
      // If the widget throws during init, this will now show up loudly
      // instead of leaving a blank container with no explanation.
      console.error("kendoGantt init failed:", err);
    }

    return () => {
      if (widgetRef.current) {
        widgetRef.current.destroy();
        widgetRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, fromDate, toDate]);

  const setAllExpanded = (expanded) => {
    const ds = widgetRef.current?.dataSource;
    if (!ds) return;
    ds.data().forEach((task) => task.set("expanded", expanded));
  };

  return (
    <div
      className="lab-gantt-kendo-jquery"
      style={{
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        fontSize: 13,
        background: "#fff",
        borderRadius: 10,
        border: "1px solid #dde3ec",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
      }}
    >
      {/* ── Header ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          background: PRIMARY_GRADIENT,
          color: "#fff",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 28,
              height: 28,
              background: "rgba(255,255,255,0.15)",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
            }}
          >
            🧪
          </div>
          <h2
            style={{
              fontSize: 15,
              fontWeight: 700,
              margin: 0,
              letterSpacing: "0.01em",
            }}
          >
            Lab Booking
          </h2>
        </div>
        {onAddClick && (
          <button
            onClick={onAddClick}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 600,
              border: "1.5px solid rgba(255,255,255,0.5)",
              borderRadius: 6,
              background: "rgba(255,255,255,0.15)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <span style={{ fontSize: 16 }}>+</span> Add
          </button>
        )}
      </div>

      {/* ── Toolbar ── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 12px",
          background: "#f8fafc",
          borderBottom: "1px solid #dde3ec",
          flexWrap: "wrap",
        }}
      >
        <label
          style={{
            fontSize: 12,
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          Start Date:
          <input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(e) => setFromDate(e.target.value)}
            onKeyDown={(e) => e.preventDefault()}
            onClick={(e) => e.currentTarget.showPicker?.()}
            style={{
              fontSize: 12,
              padding: "4px 8px",
              border: "1px solid #cbd5e1",
              borderRadius: 5,
              cursor: "pointer",
            }}
          />
        </label>
        <label
          style={{
            fontSize: 12,
            color: "#64748b",
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          End Date:
          <input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(e) => setToDate(e.target.value)}
            onKeyDown={(e) => e.preventDefault()}
            onClick={(e) => e.currentTarget.showPicker?.()}
            style={{
              fontSize: 12,
              padding: "4px 8px",
              border: "1px solid #cbd5e1",
              borderRadius: 5,
              cursor: "pointer",
            }}
          />
        </label>
        {dateError && (
          <span style={{ fontSize: 11, color: "#ef4444", fontWeight: 600, marginLeft: 4 }}>
            {dateError}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setAllExpanded(true)}
          style={{
            fontSize: 12,
            padding: "5px 12px",
            border: `1px solid ${PRIMARY}`,
            borderRadius: 5,
            background: "#fff",
            color: PRIMARY,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Expand All
        </button>
        <button
          onClick={() => setAllExpanded(false)}
          style={{
            fontSize: 12,
            padding: "5px 12px",
            border: "none",
            borderRadius: 5,
            background: PRIMARY_GRADIENT,
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Collapse All
        </button>
      </div>

      {/* ── Legend ── */}
      <div
        style={{
          display: "flex",
          gap: 16,
          padding: "6px 14px",
          background: "#f8fafc",
          borderBottom: "1px solid #edf0f5",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {[
          { l: "Location", color: TYPE_COLOR.loc },
          { l: "Facility", color: TYPE_COLOR.fac },
          { l: "Project", color: TYPE_COLOR.proj },
          { l: "Task", color: TYPE_COLOR.task },
        ].map((x) => (
          <span
            key={x.l}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              color: "#64748b",
            }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 2,
                background: x.color,
                display: "inline-block",
              }}
            />
            {x.l}
          </span>
        ))}
        <span
          style={{
            width: 1,
            height: 14,
            background: "#e2e8f0",
            display: "inline-block",
          }}
        />
        {["completed", "running", "pending"].map((s) => (
          <span
            key={s}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              fontSize: 11,
              color: "#64748b",
            }}
          >
            <span
              style={{
                width: 24,
                height: 8,
                borderRadius: 2,
                background: STATUS_COLOR[s],
                display: "inline-block",
              }}
            />
            {STATUS_BADGE[s]}
          </span>
        ))}
      </div>

      {loading && (
        <div style={{ padding: 24, textAlign: "center", color: "#64748b" }}>
          Loading…
        </div>
      )}
      {!loading && isEmpty && (
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "#94a3b8",
            fontSize: 13,
          }}
        >
          No bookings found for the selected date range.
        </div>
      )}

      {/* ── jQuery Kendo Gantt mounts here ── */}
      {!loading && !isEmpty && (
        <div ref={ganttElRef} style={{ minHeight: 500 }} />
      )}
    </div>
  );
}
